import React, { useMemo } from 'react';

import cn from 'classnames';

import { Icon } from 'components';
import { parseResponse } from 'lib/utils/parser';
import { RoomAttributes } from 'lib/models/candidate-messages';
import { MessageAttributes } from 'lib/models/message';
import { MessageAvatar } from '../MessageAvatar/MessageAvatar.component';
import { getDisplayName, getProcessedDate } from '../messagesUtils';

import styles from './ChatRoomCard.module.scss';

interface ChatRoomCardProps {
  isActive: boolean;
  room: RoomAttributes;
  onSelect: (room: RoomAttributes) => void;
}

export const ChatRoomCard: React.FC<ChatRoomCardProps> = (
  props: ChatRoomCardProps
) => {
  const { isActive, room, onSelect } = props;
  const {
    last_message: last_message_response,
    muted_at,
    is_unread,
    unread_count,
    blocked,
  } = room;
  const lastMessage = parseResponse<MessageAttributes>(last_message_response);

  const displayName = useMemo(() => {
    return getDisplayName(room);
  }, [room]);

  const lastMessageTime = useMemo(() => {
    const timestamp = lastMessage?.created_at;
    return getProcessedDate(new Date(timestamp));
  }, [room]);

  return (
    <div
      className={cn(styles.chatRoomCard, { [styles.selected]: isActive })}
      onClick={() => onSelect(room)}
    >
      <div className={styles.roomDetails}>
        <MessageAvatar room={room} />
        <div className={styles.chatRoomCardInfo}>
          <div className={styles.cardTitle}>
            <p className={styles.name}>{displayName}</p>
            {muted_at && (
              <Icon
                className={styles.attrIcon}
                iconName="icon_volume-mute"
                size="small"
              />
            )}
            {blocked && (
              <Icon
                className={styles.attrIcon}
                iconName="icon_ban"
                size="small"
              />
            )}
          </div>
          <div className={styles.lastMessage}>
            <p className={styles.message}>
              {lastMessage?.value.replaceAll('\n', ' ')}
            </p>
            {lastMessageTime && (
              <div className={styles.timestamp}>
                <div className={styles.dot}></div>
                <p className={styles.time}>{lastMessageTime}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {is_unread && (
        <div
          className={cn(styles.chatRoomCardUnread, {
            [styles.chatRoomCardUnreadMuted]: !!muted_at,
          })}
        >
          <p>{unread_count}</p>
        </div>
      )}
    </div>
  );
};
